import { DrupalNodeExternalResource } from "@/types"
import { ExternalResourceInterface } from "../drupal/external-resource"
import { CommandSearch } from "@/components/app/acli/CommandSearch.client"
import { AcquiaCliRenderer, AcquiaCliSidebarRenderer } from "@/components/app/acli/AcquiaCliRenderer"
import { PageInterface } from "@/components/page/PageInterface"
import { MetadataRoute } from "next"
import { ExternalResourceBase } from "@/components/page/external-resource"

export interface CommandNamespace {
  command: string
  path: string
  help: string
  usage: string
}

const requestOpts: NextFetchRequestConfig | RequestInit = {
  next: { revalidate: 3600 },
  // cache: 'force-cache'
}

export interface Command {
  name: string
  description: string
  usage: string[]
  help: string
  definition: {
    arguments: Record<string, Argument>
    options: Record<string, Option>
  }
  hidden: boolean
}

interface Argument {
  name: string
  is_required: boolean
  is_array: boolean
  description: string
  default: any
}

interface Option {
  name: string
  shortcut: string
  accept_value: boolean
  is_value_required: boolean
  is_multiple: boolean
  description: string
  default: any
}

export class JsonResource {
  constructor(public readonly indexEndpoint: string) {}

  getCommandEndpoint(command: string): string {
    return this.indexEndpoint.replace('index.json', command + '.json')
  }

  async getCommandIndex ():Promise<CommandNamespace[]> {
    const response = await fetch(this.indexEndpoint, {
      next: { revalidate: 3600 },
    });
    return response.json();
  }

  async getCommandData (command: string):Promise<Command|null> {
    if (command.length == 0) {
      return null
    }
    const request_uri = this.getCommandEndpoint(command)
    const response = await fetch(request_uri, {
      next: { revalidate: 3600 },
    });
    if (response.status != 200) {
      throw new Error("Command not found at: " + request_uri);
    }
    return response.json();
  }
}

export type AcliCommandsIndexContext = {
  index: CommandNamespace[]
}

export class AcquiaCliResource extends ExternalResourceBase<AcliCommandsIndexContext>  {
  private readonly resource: JsonResource
  public readonly useDocumentSidebar = true
  public readonly useTableOfContents = true

  constructor(public readonly node: DrupalNodeExternalResource) {
    super()
    this.resource = new JsonResource(node.field_resource_url.uri);
  }

  get basepath(): string {
    return this.node.path.alias
  }

  getCommandFromSlug(slug: string): string|null {
    if (slug == this.basepath) {
      return null
    }
    return decodeURIComponent(`/${slug}`).replace(this.basepath + '/', '')
  }

  async getPageTitle(slug: string): Promise<string> {
    try {

      const commandName = this.getCommandFromSlug(slug)
      if (commandName == null) {
        return this.node.title
      }
      const data =  await this.resource.getCommandData(commandName)
      return data?.name ?? this.node.title;
    }
    catch (err) {
      console.error(err);
      return this.node.title
    }
  }

  async getHeaderChildren(slug: string): Promise<JSX.Element> {
    return <CommandSearch slugBase={this.basepath} />
  }

  async getContext(): Promise<AcliCommandsIndexContext> {
    return {
      index: await this.resource.getCommandIndex()
    }
  }

  async getPageContent(props: PageInterface<DrupalNodeExternalResource>): Promise<JSX.Element> {
    return <AcquiaCliRenderer {...props} />
  }

  async getSidebar(props: PageInterface<DrupalNodeExternalResource>): Promise<JSX.Element> {
    return <AcquiaCliSidebarRenderer {...props} />
  }

  async buildSitemap(baseUrl:string): Promise<MetadataRoute.Sitemap> {
    const index = await this.resource.getCommandIndex()
    return index.map((command) => ({
        url: baseUrl + this.basepath + '/' + command.command,
        lastModified: new Date(),
        changeFrequency: 'weekly',
        priority: 0.8,
      }
    ))
  }
}